import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Download, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { GrantsSiteHeader } from "@/components/grants-site-header";
import { SiteFooter } from "@/components/site-footer";
import { supabase } from "@/integrations/supabase/client";
import { usePricing } from "@/lib/pricing";

export const Route = createFileRoute("/grants/extension")({
  head: () => ({
    meta: [
      { title: "DeckScore Grants Autofill - Chrome extension" },
      {
        name: "description",
        content:
          "Writes grant application answers from your pitch deck analysis - problem, traction, market - not just your name and email. 3 free forms a month, then a low monthly plan for 25 forms a month.",
      },
      { property: "og:title", content: "DeckScore Grants Autofill" },
      {
        property: "og:description",
        content: "Stop copy-pasting. Autofill grant forms from your pitch deck analysis.",
      },
    ],
  }),
  component: ExtensionPage,
});

function ExtensionPage() {
  const [signedIn, setSignedIn] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const pricing = usePricing();
  const [proPrice, proSuffix] = pricing.proMonthlyDisplay.split("/mo") as [string, string | undefined];
  const proSuffixLabel = proSuffix !== undefined ? "/mo" : "";

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (mounted) setSignedIn(!!data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSignedIn(!!s);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  async function upgradePro() {
    setUpgrading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-subscription", { body: { currency: pricing.currency, country: pricing.country } });
      if (error) throw error;
      const url = (data as { url?: string; error?: string })?.url;
      const errMsg = (data as { error?: string })?.error;
      if (errMsg) throw new Error(errMsg);
      if (!url) throw new Error("No checkout URL returned");
      window.location.assign(url);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not start upgrade");
    } finally {
      setUpgrading(false);
    }
  }

  function download() {
    fetch("/deckscore-grants-autofill.zip")
      .then((res) => {
        if (!res.ok) throw new Error(`Download failed: ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "deckscore-grants-autofill.zip";
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch((err) => alert(err.message));
  }

  return (
    <div className="min-h-screen bg-bg text-ink">
      <GrantsSiteHeader />
      <main className="mx-auto max-w-[960px] px-5 py-10">
        <nav className="text-[13px] text-muted-ink">
          <Link to="/grants" className="hover:text-ink">Grants</Link>
          <span className="mx-2 text-[color:var(--color-accent)]">/</span>
          <span className="text-ink">Chrome extension</span>
        </nav>

        {/* Hero */}
        <section className="mt-6 rounded-3xl border border-line bg-surface p-8 md:p-10">
          <p className="font-script text-accent text-[22px] leading-none">stop copy-pasting</p>
          <h1
            className="mt-3 text-[38px] md:text-[44px] font-bold leading-tight tracking-tight"
            style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
          >
            Autofill any grant form from your pitch deck analysis.
          </h1>
          <p className="mt-3 max-w-[680px] text-[15.5px] text-muted-ink leading-relaxed">
            It writes answers from your pitch deck analysis - problem, traction, market - not just
            your name and email. One Autofill Profile on DeckScore powers every grant application.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={download}
              className="inline-flex items-center gap-2 rounded-full bg-accent px-5 py-3 text-[14px] font-bold text-accent-ink hover:bg-accent-press"
            >
              <Download className="h-4 w-4" /> Download extension (.zip)
            </button>
            <Link
              to="/dashboard"
              search={{ tab: "autofill" }}
              className="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-5 py-3 text-[14px] font-semibold text-ink hover:bg-surface-sunken"
            >
              Edit Autofill Profile →
            </Link>
          </div>
          <p className="mt-3 text-[12.5px] text-ink-tertiary">
            Nothing is saved unless you press Remember. See and delete everything, anytime.
          </p>
        </section>

        {/* Install steps */}
        <section className="mt-10">
          <h2
            className="text-[22px] font-bold tracking-tight"
            style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
          >
            Install in 3 steps
          </h2>
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            <Step
              n={1}
              title="Download the zip"
              body="Click the download button above and unzip it somewhere you'll remember."
            />
            <Step
              n={2}
              title="Open chrome://extensions"
              body="Paste chrome://extensions into your address bar. Toggle Developer mode (top-right)."
            />
            <Step
              n={3}
              title="Load unpacked"
              body="Click Load unpacked and pick the unzipped folder. Pin the extension for one-click access."
            />
          </div>
          <p className="mt-3 text-[13px] text-ink-tertiary">
            Log into the extension with this same account.
          </p>
        </section>

        {/* Pricing */}
        <section className="mt-10 grid gap-4 md:grid-cols-2">
          <PricingCard
            name="Free"
            price="Free"
            tag="Try it"
            features={["3 forms a month", "Full profile autofill", "Works on any site"]}
            cta={
              <button
                type="button"
                onClick={download}
                className="inline-flex w-full items-center justify-center rounded-full border border-line px-4 py-2.5 text-[13.5px] font-semibold text-ink hover:bg-surface-sunken"
              >
                Download free
              </button>
            }
          />
          <PricingCard
            name="Pro"
            price={proPrice}
            priceSuffix={proSuffixLabel}
            tag="Recommended"
            highlighted
            features={[
              "25 forms a month",
              "Priority updates & new form templates",
              "Same account as DeckScore",
            ]}
            cta={
              <div className="w-full">
                {signedIn ? (
                  <button
                    type="button"
                    onClick={upgradePro}
                    disabled={upgrading}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-accent px-4 py-2.5 text-[13.5px] font-bold text-accent-ink hover:bg-accent-press disabled:opacity-70"
                  >
                    {upgrading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Starting checkout…
                      </>
                    ) : (
                      <>Upgrade to Pro →</>
                    )}
                  </button>
                ) : (
                  <Link
                    to="/login"
                    search={{ next: "/grants/extension" }}
                    className="inline-flex w-full items-center justify-center rounded-full bg-accent px-4 py-2.5 text-[13.5px] font-bold text-accent-ink hover:bg-accent-press"
                  >
                    Sign in to upgrade →
                  </Link>
                )}
                <p className="mt-2 text-center text-[11.5px] opacity-70">
                  Pro activates automatically within a minute of payment.
                </p>
              </div>
            }
          />

        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function Step({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-line bg-surface p-5">
      <div className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-ink text-[13px] font-bold text-[color:var(--color-bg)]">
        {n}
      </div>
      <h3 className="mt-3 text-[15.5px] font-semibold">{title}</h3>
      <p className="mt-1 text-[13.5px] text-muted-ink leading-relaxed">{body}</p>
    </div>
  );
}

function PricingCard({
  name,
  price,
  priceSuffix,
  tag,
  features,
  cta,
  highlighted,
}: {
  name: string;
  price: string;
  priceSuffix?: string;
  tag?: string;
  features: string[];
  cta: React.ReactNode;
  highlighted?: boolean;
}) {
  return (
    <div
      className={`rounded-2xl border p-6 ${
        highlighted ? "border-accent bg-ink text-[color:var(--color-bg)]" : "border-line bg-surface text-ink"
      }`}
    >
      <div className="flex items-center justify-between">
        <div
          className="text-[16px] font-bold"
          style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
        >
          {name}
        </div>
        {tag && (
          <span
            className={`rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wider ${
              highlighted
                ? "bg-[color:var(--color-accent-lavender)] text-ink"
                : "bg-surface-sunken text-ink-secondary"
            }`}
          >
            {tag}
          </span>
        )}
      </div>
      <div className="mt-4 flex items-baseline gap-1">
        <span
          className="text-[36px] font-bold leading-none"
          style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
        >
          {price}
        </span>
        {priceSuffix && <span className="text-[13.5px] opacity-70">{priceSuffix}</span>}
      </div>
      <ul className="mt-4 flex flex-col gap-2 text-[13.5px]">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check
              className={`mt-0.5 h-4 w-4 flex-none ${highlighted ? "text-[color:var(--color-accent-mint)]" : "text-accent"}`}
            />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className="mt-6">{cta}</div>
    </div>
  );
}
